/* eslint-disable react-refresh/only-export-components */
import { useEffect, useState, useCallback, useMemo } from 'react';
import { CheckCircleIcon, XCircleIcon, ExclamationTriangleIcon, InformationCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

interface ToastProps {
    type: ToastType;
    message: string;
    onClose: () => void;
    autoClose?: boolean;
    autoCloseDelay?: number;
}

export default function Toast({ type, message, onClose, autoClose = true, autoCloseDelay = 5000 }: ToastProps) {
    useEffect(() => {
        if (autoClose) {
            const timer = setTimeout(onClose, autoCloseDelay);
            return () => clearTimeout(timer);
        }
    }, [autoClose, autoCloseDelay, onClose]);

    const getStyles = () => {
        const baseStyles = 'p-4 rounded-lg border shadow-lg flex items-start gap-3 min-w-[300px] max-w-md';
        switch (type) {
            case 'success':
                return `${baseStyles} bg-green-50 border-green-200 text-green-800`;
            case 'error':
                return `${baseStyles} bg-red-50 border-red-200 text-red-800`;
            case 'warning':
                return `${baseStyles} bg-yellow-50 border-yellow-200 text-yellow-800`;
            case 'info':
                return `${baseStyles} bg-blue-50 border-blue-200 text-blue-800`;
            default:
                return `${baseStyles} bg-gray-50 border-gray-200 text-gray-800`;
        }
    };

    const getIcon = () => {
        const iconClass = 'w-5 h-5 flex-shrink-0';
        switch (type) {
            case 'success':
                return <CheckCircleIcon className={`${iconClass} text-green-600`} />;
            case 'error':
                return <XCircleIcon className={`${iconClass} text-red-600`} />;
            case 'warning':
                return <ExclamationTriangleIcon className={`${iconClass} text-yellow-600`} />;
            case 'info':
                return <InformationCircleIcon className={`${iconClass} text-blue-600`} />;
            default:
                return null;
        }
    };

    return (
        <div className={getStyles()}>
            {getIcon()}
            <div className="flex-1">
                <p className="text-sm font-medium">{message}</p>
            </div>
            <button
                onClick={onClose}
                className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Fechar"
            >
                <XMarkIcon className="w-5 h-5" />
            </button>
        </div>
    );
}

interface ToastContainerProps {
    toasts: Array<{ id: string; type: ToastType; message: string }>;
    onRemove?: (id: string) => void;
    onClose?: (id: string) => void;
}

export function ToastContainer({ toasts, onRemove, onClose }: ToastContainerProps) {
    if (!toasts || toasts.length === 0) return null;
    const handleRemove = onRemove || onClose || (() => {});

    return (
        <div className="fixed top-4 right-4 z-[9999] space-y-2">
            {toasts.map((toast) => (
                <Toast
                    key={toast.id}
                    type={toast.type}
                    message={toast.message}
                    onClose={() => handleRemove(toast.id)}
                />
            ))}
        </div>
    );
}

// Hook para gerenciar toasts
export function useToast() {
    const [toasts, setToasts] = useState<Array<{ id: string; type: ToastType; message: string }>>([]);

    const showToast = useCallback((type: ToastType, message: string) => {
        const id = Date.now().toString() + Math.random().toString(36).substring(2, 5);
        setToasts((prev) => [...prev, { id, type, message }]);
    }, []);

    const removeToast = useCallback((id: string) => {
        setToasts((prev) => prev.filter((toast) => toast.id !== id));
    }, []);

    const success = useCallback((message: string) => showToast('success', message), [showToast]);
    const error = useCallback((message: string) => showToast('error', message), [showToast]);
    const warning = useCallback((message: string) => showToast('warning', message), [showToast]);
    const info = useCallback((message: string) => showToast('info', message), [showToast]);

    return useMemo(() => ({
        toasts,
        showToast,
        removeToast,
        success,
        error,
        warning,
        info,
    }), [toasts, showToast, removeToast, success, error, warning, info]);
}
